/**
 * What the page already holds that can put a name on a step's object
 * (docs/plans/thread-activity-steps.md, D5). Every list is optional: the page
 * passes what it has loaded, and an absent list just means nothing resolves.
 */
export interface ActivityPageData {
  /** Population token (`AudienceSummary.id`) to display name. */
  audiences?: Array<{ id: string; name: string }>
  /** Knowledge document token (a uuid) to its display name. */
  documents?: Array<{ token: string; name: string | null | undefined }>
  /** Living artifact `publicId` to its title. */
  artifacts?: Array<{ publicId: string; title: string }>
  /** Workflow template slug to its display name. */
  workflows?: Array<{ slug: string; displayName: string }>
  /** Workflow run token to a name the reader recognises. */
  runs?: Array<{ token: string; name: string | null | undefined }>
}

/**
 * How the row shows the object:
 * - `name`: a display name resolved from page data, shown as is.
 * - `quote`: the model's own safe text (an artifact title it is creating, the
 *   reader's own revision instruction), shown in quotes.
 * - `search`: the model's literal search text, shown in quotes after the verb.
 */
export interface ActivityObject {
  kind: 'name' | 'quote' | 'search'
  text: string
}

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Tools whose `query` is the model's search text (F12). */
const SEARCH_TOOLS = new Set(['web_search', 'document_search'])

/**
 * Tools whose `query` is human text, safe to print: `create_artifact` sends
 * the title it is creating, `revise_run_step` echoes the reader's instruction.
 */
const QUOTE_TOOLS = new Set(['create_artifact', 'revise_run_step'])

function sameToken(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

function pickName(value: string | null | undefined): string | null {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed ? trimmed : null
}

/**
 * The object a step names, or null for the verb alone.
 *
 * Every other `query` on the wire is an identifier: a population token for
 * the audience tools, a document uuid for `read_document`, a run token for
 * `read_results`, a `publicId` for the artifact tools and a slug for
 * `read_workflow` / `run_workflow` (`_tool_activity_query` in
 * `apps/python-api/api/assistant_turn.py`). Those are matched against page
 * data, and when nothing matches the row shows no object at all. The raw id
 * never reaches the screen (F6). `read_skill`, `read_room`, `read_study`,
 * `ask_study` and `read_tracker_posts` have no page list to match yet, so
 * they always read as the verb alone, as does any tool this build does not
 * know.
 */
export function resolveActivityObject(
  tool: string,
  query: string | null | undefined,
  pageData: ActivityPageData | null | undefined,
): ActivityObject | null {
  const raw = typeof query === 'string' ? query.trim().replace(/\s+/g, ' ') : ''
  if (!raw) {
    return null
  }

  if (SEARCH_TOOLS.has(tool)) {
    return { kind: 'search', text: raw }
  }

  /* With a Topic its `query` is the Topic's token, never shown. Without one, the words searched. */
  if (tool === 'search_social_posts') {
    return UUID_SHAPE.test(raw) ? null : { kind: 'search', text: raw }
  }

  if (QUOTE_TOOLS.has(tool)) {
    return { kind: 'quote', text: raw }
  }

  const data = pageData ?? {}
  let name: string | null = null

  if (tool === 'read_audience' || tool === 'analyze_audience') {
    name = pickName(data.audiences?.find((entry) => sameToken(entry.id, raw))?.name)
  } else if (tool === 'read_document') {
    name = pickName(
      data.documents?.find((entry) => sameToken(entry.token, raw))?.name,
    )
  } else if (
    tool === 'read_artifact' ||
    tool === 'update_artifact' ||
    tool === 'delete_artifact'
  ) {
    name = pickName(
      data.artifacts?.find((entry) => entry.publicId === raw)?.title,
    )
  } else if (tool === 'read_workflow' || tool === 'run_workflow') {
    name = pickName(
      data.workflows?.find((entry) => entry.slug === raw)?.displayName,
    )
  } else if (tool === 'read_results') {
    name = pickName(data.runs?.find((entry) => entry.token === raw)?.name)
  }

  return name ? { kind: 'name', text: name } : null
}
